import { useId, useState } from 'react';
import { campaignCopy } from '@discovr/features';
import type { HoldReasonCode } from '@discovr/types';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { TypedConfirmField, isTypedConfirmMatch } from '../../components/common/TypedConfirmField';
import { useLastNonEmpty } from '../hooks/useLastNonEmpty';
import { HOLD_REASONS, holdNoteError, holdNoteLengthError, isHoldNoteMissing } from './ConfirmedTab.holdReasons';
import { HoldNoteField } from './HoldNoteField';

const copy = campaignCopy.creator.hold;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  creatorName: string;
  busy: boolean;
  onCloseAutoFocus?: (event: Event) => void;
  onConfirm: (reason: HoldReasonCode, note: string) => void | Promise<void>;
}

export function HoldPostDialog({ open, onOpenChange, creatorName, busy, onConfirm, onCloseAutoFocus }: Props) {
  const [reason, setReason] = useState<HoldReasonCode | ''>('');
  const [note, setNote] = useState('');
  const [noteTouched, setNoteTouched] = useState(false);
  const [typedConfirm, setTypedConfirm] = useState('');
  const reasonId = useId();
  const shownName = useLastNonEmpty(creatorName);

  const noteMissing = isHoldNoteMissing(reason, note);
  // The Other requirement stays a hint until the user has been in the note field.
  const error = holdNoteLengthError(note) ?? (noteMissing && noteTouched ? copy.note.required : null);
  const hint = noteMissing && !noteTouched ? copy.note.required : null;
  const canSubmit =
    reason !== '' && holdNoteError(reason, note) === null && isTypedConfirmMatch(typedConfirm) && !busy;

  const reset = () => {
    setReason('');
    setNote('');
    setNoteTouched(false);
    setTypedConfirm('');
  };
  if (!open && (reason !== '' || note !== '' || noteTouched || typedConfirm !== '')) reset();

  const submit = () => {
    if (canSubmit && reason) void onConfirm(reason, note.trim());
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md" onCloseAutoFocus={onCloseAutoFocus}>
        <DialogHeader>
          <DialogTitle>{copy.dialog.title(shownName)}</DialogTitle>
          <DialogDescription>{copy.dialog.description}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <label htmlFor={reasonId} className="text-sm font-medium">
              {copy.dialog.reasonLabel}
            </label>
            <Select value={reason} onValueChange={(v) => setReason(v as HoldReasonCode)}>
              <SelectTrigger id={reasonId} aria-label={copy.dialog.reasonLabel}>
                <SelectValue placeholder={copy.dialog.reasonPlaceholder} />
              </SelectTrigger>
              <SelectContent>
                {HOLD_REASONS.map((r) => (
                  <SelectItem key={r.code} value={r.code}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <HoldNoteField
            label={reason === 'other' ? copy.note.labelRequired : copy.note.labelOptional}
            value={note}
            onChange={(value) => {
              setNote(value);
              setNoteTouched(true);
            }}
            onBlur={() => setNoteTouched(true)}
            error={error}
            hint={hint}
          />
          <TypedConfirmField
            value={typedConfirm}
            onChange={setTypedConfirm}
            disabled={busy}
            onEnter={submit}
            helperText={copy.dialog.confirmHelper}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={busy}>
            {copy.dialog.cancel}
          </Button>
          <Button disabled={!canSubmit} onClick={submit}>
            {copy.dialog.confirm}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
